// Shared status/row-type metadata for inbound delivery notes (sähköiset lähetteet).
// Colors follow the claude_designs/sahkoiset-lahetteet reference palette.

import { formatPrice } from '../../../utils/vat';

export const NOTE_STATUS_META = {
  new: { labelKey: 'inboundDeliveries.status.new', fg: '#2c5282', bg: '#ebf4ff' },
  reviewed: { labelKey: 'inboundDeliveries.status.reviewed', fg: '#553c9a', bg: '#f5f0ff' },
  allocated: { labelKey: 'inboundDeliveries.status.allocated', fg: '#2f8c54', bg: '#e6ffed' },
  dismissed: { labelKey: 'inboundDeliveries.status.dismissed', fg: '#64748b', bg: '#f1f5f9' },
};

export const PROCESSING_META = {
  pending: { labelKey: 'inboundDeliveries.processing.pending', fg: '#64748b', bg: '#f1f5f9' },
  processing: { labelKey: 'inboundDeliveries.processing.processing', fg: '#b45309', bg: '#fef3e2' },
  done: { labelKey: 'inboundDeliveries.processing.done', fg: '#2f8c54', bg: '#e6ffed' },
  failed: { labelKey: 'inboundDeliveries.processing.failed', fg: '#c53030', bg: '#fee2e2' },
};

export const RESOLUTION_META = {
  pending: { labelKey: 'inboundDeliveries.resolution.pending', fg: '#64748b', bg: '#f1f5f9' },
  matched_order_received: { labelKey: 'inboundDeliveries.resolution.matched_order_received', fg: '#2f8c54', bg: '#e6ffed' },
  marked_received: { labelKey: 'inboundDeliveries.resolution.marked_received', fg: '#2f8c54', bg: '#e6ffed' },
  attached_new: { labelKey: 'inboundDeliveries.resolution.attached_new', fg: '#2f8c54', bg: '#e6ffed' },
  booked_to_stock: { labelKey: 'inboundDeliveries.resolution.booked_to_stock', fg: '#2f8c54', bg: '#e6ffed' },
  skipped: { labelKey: 'inboundDeliveries.resolution.skipped', fg: '#64748b', bg: '#f1f5f9' },
};

// A credit row (hyvitys) resolves through the same states but means the opposite
// stock movement — label it as a return so the ledger and the chip agree.
export const CREDIT_RESOLUTION_META = {
  booked_to_stock: { labelKey: 'inboundDeliveries.resolution.returned_to_stock', fg: '#553c9a', bg: '#f5f0ff' },
  attached_new: { labelKey: 'inboundDeliveries.resolution.attached_credit', fg: '#553c9a', bg: '#f5f0ff' },
};

export const ROW_TYPE_LABEL_KEYS = {
  part: 'inboundDeliveries.rowType.part',
  freight: 'inboundDeliveries.rowType.freight',
  fee: 'inboundDeliveries.rowType.fee',
  labor: 'inboundDeliveries.rowType.labor',
  other: 'inboundDeliveries.rowType.other',
};

// Machine-readable validation flag codes → i18n keys
export const FLAG_LABEL_KEYS = {
  qty_price_mismatch: 'inboundDeliveries.flags.qty_price_mismatch',
  sum_mismatch: 'inboundDeliveries.flags.sum_mismatch',
  possible_duplicate: 'inboundDeliveries.flags.possible_duplicate',
  reg_format: 'inboundDeliveries.flags.reg_format',
  low_confidence: 'inboundDeliveries.flags.low_confidence',
  vat_basis_corrected: 'inboundDeliveries.flags.vat_basis_corrected',
};

// Flags that report a correction we made, not a problem the user must fix —
// rendered in neutral blue instead of the warning red.
export const INFO_FLAGS = new Set(['vat_basis_corrected']);

// Suggestion/match reason codes → i18n keys (params interpolated via i18next)
export const REASON_LABEL_KEYS = {
  po_number_match: 'inboundDeliveries.reasons.po_number_match',
  order_reference_match: 'inboundDeliveries.reasons.order_reference_match',
  vendor_match: 'inboundDeliveries.reasons.vendor_match',
  registration_match: 'inboundDeliveries.reasons.registration_match',
  order_line_code_match: 'inboundDeliveries.reasons.order_line_code_match',
  wo_reg_match: 'inboundDeliveries.reasons.wo_reg_match',
  wo_part_code_match: 'inboundDeliveries.reasons.wo_part_code_match',
  wo_part_price_diff: 'inboundDeliveries.reasons.wo_part_price_diff',
  wo_terminal_state: 'inboundDeliveries.reasons.wo_terminal_state',
  no_wo_part_match: 'inboundDeliveries.reasons.no_wo_part_match',
  non_part_row: 'inboundDeliveries.reasons.non_part_row',
  part_catalog_match: 'inboundDeliveries.reasons.part_catalog_match',
  also_references: 'inboundDeliveries.reasons.also_references',
  no_line_overlap: 'inboundDeliveries.reasons.no_line_overlap',
  order_line_overlap: 'inboundDeliveries.reasons.order_line_overlap',
};

export const TERMINAL_WO_STATES = ['completed', 'picked_up', 'cancelled'];

// Backend reason params arrive as raw decimal strings ("209.23"); money goes
// through formatPrice here so every amount on screen is fi-formatted.
const MONEY_PARAMS = new Set(['old_price', 'new_price']);

export function translateReason(t, reason) {
  if (!reason || !reason.code) return null;
  const key = REASON_LABEL_KEYS[reason.code];
  if (!key) return null;
  const params = {};
  Object.entries(reason.params || {}).forEach(([name, value]) => {
    params[name] = MONEY_PARAMS.has(name) && value != null && value !== '' ? formatPrice(value) : value;
  });
  return t(key, params);
}
